import { registerWorkspaceAction, openAddonPane, resetAddonWorkspaceActionsForTests } from './addon-workspace-actions.js';
import { paneRegistry } from '../panes/index.js';
import type { ComposerDictationProvider } from './composer-dictation.js';
import { registerOwnedDictationProvider, selectOwnedDictationProvider, resetDictationProvidersForTests } from './composer-dictation-registry.js';
export type { ComposerDictationProvider, DictationSession, DictationState, DictationFinish } from './composer-dictation.js';
import { registerSettingsPane, unregisterSettingsPane, notifySettingsPanesChanged } from '../components/settings/pane-registry.js';

export interface AddonStandaloneTabUrlContext {
  hasPopOutTab?: boolean;
}

export interface AddonAttachmentPreviewDefinition {
  id: string;
  label: string;
  match: (contentType: unknown, filename?: unknown) => boolean;
  buildFrameUrl: (mediaId: number | string, filename?: string) => string | null;
  note?: string | null;
}

export interface AddonWebApiSurface {
  workspaceActionsVersion: 1;
  composerDictationVersion: 1;
  registerComposerDictationProvider(provider: ComposerDictationProvider): () => void;
  selectComposerDictationProvider(id: string | null): void;
  registerWorkspaceAction: typeof registerWorkspaceAction;
  openPane: typeof openAddonPane;
  registerPane: (extension: any) => boolean;
  registerWorkspacePane: (extension: any) => boolean;
  registerSettingsPane: (definition: any) => () => void;
  registerStandaloneTabUrlResolver: (resolver: (path: string, context?: AddonStandaloneTabUrlContext) => string | null | undefined) => () => void;
  registerAttachmentPreview: (definition: AddonAttachmentPreviewDefinition) => () => void;
  getCurrentChatJid: () => string;
}

const addonPaneIds = new Set<string>();
const addonSettingsPaneIds = new Set<string>();
const standaloneTabUrlResolvers = new Set<(path: string, context?: AddonStandaloneTabUrlContext) => string | null | undefined>();
const attachmentPreviewDefinitions = new Map<string, AddonAttachmentPreviewDefinition>();
let addonWebApiInstalled = false;
let addonWebEntryLoadPromise: Promise<void> | null = null;

function resolveCurrentChatJid(runtimeWindow: (Window & typeof globalThis) | null = typeof window !== 'undefined' ? window : null): string {
  const globalValue = typeof (runtimeWindow as any)?.__piclawCurrentChatJid === 'string'
    ? (runtimeWindow as any).__piclawCurrentChatJid.trim()
    : '';
  if (globalValue) return globalValue;
  try {
    const href = runtimeWindow?.location?.href || 'http://localhost/';
    const fromUrl = new URL(href).searchParams.get('chat_jid')?.trim() || '';
    if (fromUrl) return fromUrl;
  } catch (e) {
    // ignore and fall back
    void e;
  }
  return 'web:default';
}

function normalizeUrl(value: unknown, base: string): string | null {
  const input = typeof value === 'string' ? value.trim() : '';
  if (!input) return null;
  try {
    return new URL(input, base).href;
  } catch {
    return null;
  }
}

export function registerAddonWorkspacePane(extension: any): boolean {
  if (!extension || typeof extension.id !== 'string' || !extension.id.trim()) return false;
  paneRegistry.register(extension, { addon: true });
  addonPaneIds.add(extension.id);
  return true;
}

export function registerAddonPane(extension: any): boolean {
  return registerAddonWorkspacePane(extension);
}

export function registerAddonSettingsPane(definition: any): () => void {
  if (!definition || typeof definition.id !== 'string' || !definition.id.trim()) {
    return () => {};
  }
  registerSettingsPane(definition);
  addonSettingsPaneIds.add(definition.id);
  notifySettingsPanesChanged();
  return () => {
    unregisterSettingsPane(definition.id);
    addonSettingsPaneIds.delete(definition.id);
    notifySettingsPanesChanged();
  };
}

export function registerAddonStandaloneTabUrlResolver(
  resolver: (path: string, context?: AddonStandaloneTabUrlContext) => string | null | undefined,
): () => void {
  if (typeof resolver !== 'function') return () => {};
  standaloneTabUrlResolvers.add(resolver);
  return () => {
    standaloneTabUrlResolvers.delete(resolver);
  };
}

export function resolveAddonStandaloneTabUrl(path: string, context: AddonStandaloneTabUrlContext = {}): string | null {
  const normalizedPath = typeof path === 'string' ? path.trim() : '';
  if (!normalizedPath) return null;
  for (const resolver of [...standaloneTabUrlResolvers].reverse()) {
    try {
      const resolved = resolver(normalizedPath, context);
      if (typeof resolved === 'string' && resolved.trim()) return resolved.trim();
    } catch (error) {
      console.warn('[addon-web] standalone tab URL resolver failed:', error);
    }
  }
  return null;
}

export function registerAddonAttachmentPreview(definition: AddonAttachmentPreviewDefinition): () => void {
  if (!definition || typeof definition.id !== 'string' || !definition.id.trim() || typeof definition.match !== 'function' || typeof definition.buildFrameUrl !== 'function') {
    return () => {};
  }
  attachmentPreviewDefinitions.set(definition.id, definition);
  return () => {
    if (attachmentPreviewDefinitions.get(definition.id) === definition) {
      attachmentPreviewDefinitions.delete(definition.id);
    }
  };
}

export function resolveAddonAttachmentPreview(contentType: unknown, filename?: unknown): AddonAttachmentPreviewDefinition | null {
  for (const definition of Array.from(attachmentPreviewDefinitions.values()).reverse()) {
    try {
      if (definition.match(contentType, filename)) return definition;
    } catch (error) {
      console.warn('[addon-web] attachment preview matcher failed:', error);
    }
  }
  return null;
}

export function getAddonAttachmentPreviewLabel(kind: string | null | undefined): string | null {
  const normalizedKind = typeof kind === 'string' ? kind.trim() : '';
  if (!normalizedKind) return null;
  return attachmentPreviewDefinitions.get(normalizedKind)?.label || null;
}

export function getAddonAttachmentPreviewNote(kind: string | null | undefined): string | null {
  const normalizedKind = typeof kind === 'string' ? kind.trim() : '';
  if (!normalizedKind) return null;
  return attachmentPreviewDefinitions.get(normalizedKind)?.note || null;
}

export function buildAddonAttachmentPreviewFrameUrl(kind: string | null | undefined, mediaId: number | string, filename?: string): string | null {
  const normalizedKind = typeof kind === 'string' ? kind.trim() : '';
  if (!normalizedKind) return null;
  const definition = attachmentPreviewDefinitions.get(normalizedKind);
  if (!definition) return null;
  try {
    return definition.buildFrameUrl(mediaId, filename) || null;
  } catch (error) {
    console.warn('[addon-web] attachment preview URL builder failed:', error);
    return null;
  }
}

export function createAddonWebApi(runtimeWindow: (Window & typeof globalThis) | null = typeof window !== 'undefined' ? window : null, owner?: string, dictationRollback?: Array<() => void>): AddonWebApiSurface {
  return {
    workspaceActionsVersion: 1,
    composerDictationVersion: 1,
    registerComposerDictationProvider(provider) { if (!owner) throw Error('Dictation registration requires a loaded add-on entry.'); const dispose = registerOwnedDictationProvider(owner, provider); dictationRollback?.push(dispose); return dispose; },
    selectComposerDictationProvider(id) { if (!owner) throw Error('Dictation selection requires a loaded add-on entry.'); selectOwnedDictationProvider(owner, id); },
    registerWorkspaceAction,
    openPane: openAddonPane,
    registerPane: registerAddonPane,
    registerWorkspacePane: registerAddonWorkspacePane,
    registerSettingsPane: registerAddonSettingsPane,
    registerStandaloneTabUrlResolver: registerAddonStandaloneTabUrlResolver,
    registerAttachmentPreview: registerAddonAttachmentPreview,
    getCurrentChatJid: () => resolveCurrentChatJid(runtimeWindow),
  };
}

export function installAddonWebApi(runtimeWindow: (Window & typeof globalThis) | null = typeof window !== 'undefined' ? window : null): AddonWebApiSurface {
  const api = createAddonWebApi(runtimeWindow);
  if (!runtimeWindow || addonWebApiInstalled) return api;
  (runtimeWindow as any).__piclaw_web = api;
  (runtimeWindow as any).__piclaw_registerPane = api.registerPane;
  (runtimeWindow as any).__piclaw_registerWorkspacePane = api.registerWorkspacePane;
  (runtimeWindow as any).__piclaw_registerSettingsPane = api.registerSettingsPane;
  (runtimeWindow as any).__piclaw_registerStandaloneTabUrlResolver = api.registerStandaloneTabUrlResolver;
  (runtimeWindow as any).__piclaw_registerAttachmentPreview = api.registerAttachmentPreview;
  // Aliases used by addons (observability, cheapskate, sample-addon)
  (runtimeWindow as any).__piclawSettingsPaneRegistry = {
    registerSettingsPane: api.registerSettingsPane,
    notifySettingsPanesChanged: () => runtimeWindow?.dispatchEvent?.(new CustomEvent('piclaw:settings-panes-changed')),
  };
  addonWebApiInstalled = true;
  return api;
}

export async function loadInstalledAddonWebEntries(runtimeWindow: (Window & typeof globalThis) | null = typeof window !== 'undefined' ? window : null): Promise<void> {
  if (!runtimeWindow) return;
  if (addonWebEntryLoadPromise) return addonWebEntryLoadPromise;

  addonWebEntryLoadPromise = (async () => {
    installAddonWebApi(runtimeWindow);
    try {
      const response = await fetch('/agent/addons/web-entries', { credentials: 'same-origin' });
      if (!response.ok) return;
      const payload = await response.json().catch(() => null);
      const entries = Array.isArray(payload?.entries) ? payload.entries : [];
      const origin = runtimeWindow.location?.origin || 'http://localhost';
      for (const entry of entries) {
        const href = normalizeUrl(entry?.url, origin);
        if (!href) continue;
        const dictationRollback: Array<() => void> = [];
        try {
          const module = await import(/* @vite-ignore */ href);
          // New providers use an explicit owner-bound entry callback; legacy side-effect entries still load unchanged.
          if (typeof module.register === 'function' && typeof entry.packageName === 'string') await module.register(createAddonWebApi(runtimeWindow, entry.packageName, dictationRollback));
        } catch (error) {
          for (const dispose of dictationRollback.reverse()) dispose();
          console.warn('[addon-web] Failed to load installed addon web entry:', href, error);
        }
      }
    } catch (error) {
      console.warn('[addon-web] Failed to fetch installed addon web entries:', error);
    }
  })();

  return addonWebEntryLoadPromise;
}

export function resetAddonWebRegistriesForTests(): void {
  resetAddonWorkspaceActionsForTests();
  resetDictationProvidersForTests();
  for (const paneId of addonPaneIds) {
    paneRegistry.unregister(paneId);
  }
  for (const paneId of addonSettingsPaneIds) {
    unregisterSettingsPane(paneId);
  }
  addonPaneIds.clear();
  addonSettingsPaneIds.clear();
  notifySettingsPanesChanged();
  standaloneTabUrlResolvers.clear();
  attachmentPreviewDefinitions.clear();
  addonWebEntryLoadPromise = null;
  addonWebApiInstalled = false;
}
